import { Avatar, AvatarGroup, Box, Button, CardContent, Grid, Stack, Tooltip, Typography } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import { trim } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize, stringToColor } from '../../../helpers/format';
import useUserRoles from '../../../hooks/useUserRoles';
import { OrderCardProps } from './OrderCard';
import useOrderCardService from './useOrderCardService';

const OrderQuoteCardContent: React.FC<OrderCardProps> = ({ order, action = true, menu = true, showLink = true }) => {
  const [t] = useTranslation();
  const userRoles = useUserRoles();
  const { engagementDate, orderConsultantTooltipTitle, orderClient } = useOrderCardService(order);

  return (
    <CardContent>
      <Grid container spacing={4} sx={{ justifyContent: 'space-between' }}>
        <Grid item xs={12} md={6} lg={6}>
          <Grid
            container
            spacing={4}
            sx={{ '& .MuiGrid-item': { width: 0 }, justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Grid item xs={12} md={6} lg={2}>
              {order.affair ? (
                <Tooltip title={trim([order.affair?.otp, order.affair?.name].join(' - '))}>
                  <div>
                    <Typography noWrap className="order-card-title">
                      {order.affair?.otp}
                    </Typography>
                    <Typography noWrap className="order-card-title">
                      {order.affair?.name}
                    </Typography>
                  </div>
                </Tooltip>
              ) : (
                <Typography noWrap>{t('Affair_Not_Indicated')} </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={2}>
              <Typography noWrap className="order-card-label">
                {t('Client')}
              </Typography>
              <Tooltip title={orderClient()}>
                <Typography noWrap className="order-card-value">
                  {orderClient()}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={6} lg={2}>
              <Typography className="order-card-label">{t('Project_Name')}</Typography>
              <Tooltip title={order?.name_spec}>
                <Typography noWrap className="order-card-value">
                  {order?.name_spec || 'Not defined'}
                </Typography>
              </Tooltip>
            </Grid>
             <Grid item xs={12} md={6} lg={2} sx={{ display: 'none' }}>
              <Typography className="order-card-label">{t('Chrono reference')}</Typography>
              <Tooltip title={order.draft_reference ?? t<string>('Not_Defined')}>
                {order.draft_reference ? (
                  <Typography noWrap className="order-card-value">
                    {order.draft_reference}
                  </Typography>
                ) : (
                  <RemoveIcon color="disabled" />
                )}
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={6} lg={2}>
              <Typography className="order-card-label">{t('Engagement_Date')}</Typography>
              <Tooltip title={engagementDate()}>
                <Typography noWrap className="order-card-value">
                  {engagementDate()}
                </Typography>
              </Tooltip>
            </Grid>
            {!userRoles.isConsultant && !userRoles.isCustomer && (
              <Grid item xs={12} md={6} lg={2}>
                <Tooltip placement="bottom-start" title={orderConsultantTooltipTitle}>
                  <Box sx={{ minHeight: '24px', display: 'flex', alignItems: 'center' }}>
                    {order.consultants && order.consultants[0] ? (
                      <AvatarGroup max={3}>
                        {order.consultants.map((c) =>
                          c.avatar_uri ? (
                            <Avatar src={c.avatar_uri} key={c.id} />
                          ) : (
                            <Avatar
                              sx={{
                                backgroundColor: stringToColor(c.first_name),
                                color: stringToColor(c.first_name, 'color'),
                                width: 28,
                                height: 28,
                              }}
                              key={c.id}
                            >{`${capitalize(c.first_name.charAt(0))}${capitalize(c.last_name.charAt(0))}`}</Avatar>
                          )
                        )}
                      </AvatarGroup>
                    ) : (
                      <Typography className="order-card-value">-</Typography>
                    )}
                  </Box>
                </Tooltip>
                <Typography className="order-card-label">{t('Consultants')}</Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </CardContent>
  );
};

export default OrderQuoteCardContent;
